import { useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FilterSection } from './FilterSection';
import { POKEMON_LANGUAGES } from '@/constants/pokemonLanguages';
import { cn } from '@/lib/utils';
import { toolbarControl } from '@/lib/buttonStyles';

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** When provided, only languages in this list are rendered. */
  availableLanguages?: string[];
}

/**
 * Canonical Language multi-select used by every desktop filter tray.
 * Replaces the two near-identical hand-rolled popovers that used to live
 * inside InventoryFilterPanel and StorefrontFilterPanel.
 */
export function LanguageMultiSelectSection({ value, onChange, availableLanguages }: Props) {
  const [open, setOpen] = useState(false);

  // When availableLanguages is provided (even as []), respect it strictly so we
  // never offer a language the seller has zero listed inventory in.
  const options = availableLanguages !== undefined
    ? POKEMON_LANGUAGES.filter((l) => availableLanguages.includes(l.value))
    : POKEMON_LANGUAGES;

  // Hide the entire section when the seller has no language data to filter on.
  if (availableLanguages !== undefined && options.length === 0) return null;

  const toggle = (lang: string) => {
    onChange(value.includes(lang) ? value.filter((l) => l !== lang) : [...value, lang]);
  };

  const triggerLabel =
    value.length === 0
      ? 'All Languages'
      : value.length === 1
        ? POKEMON_LANGUAGES.find((l) => l.value === value[0])?.label ?? '1 language'
        : `${value.length} languages`;

  return (
    <FilterSection title="Language">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              'w-full justify-between h-9 !bg-[hsl(var(--toolbar-control-bg,var(--background)))]',
              toolbarControl,
            )}
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown
              className={cn(
                'w-4 h-4 shrink-0 opacity-50 transition-colors',
                open && 'opacity-100 text-foreground',
              )}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-2 z-50 dropdown-surface border-white/[0.08] max-h-[280px] overflow-y-auto"
          align="start"
        >
          <div className="space-y-1">
            {options.map((lang) => (
              <div
                key={lang.value}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer text-sm hover:bg-white/[0.05]',
                  value.includes(lang.value) && 'bg-white/[0.08]',
                )}
                onClick={() => toggle(lang.value)}
              >
                <Checkbox checked={value.includes(lang.value)} className="h-4 w-4" />
                <span className="text-base">{lang.flag}</span>
                <span className="flex-1">{lang.label}</span>
              </div>
            ))}
          </div>
          {value.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/[0.08]">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onChange([])}
                className="w-full text-xs"
              >
                Clear Languages
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </FilterSection>
  );
}
